import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  RACTable,
  RACTableCell,
  RACTableRow,
  Typography,
  RACButton,
  RACCheckBox,
  RACTextField,
  RACModalCard,
  CircularProgress,
  Box,
  Tooltip,
} from '@rentacenter/racstrap';
import { useTranslation } from 'react-i18next';
import { ReactComponent as EditIcon } from '../../../src/assets/images/edit 1.svg';
import { ReactComponent as TickIcon } from '../../emilyIcons/Record tick.svg';
import { ReactComponent as DoneIcon } from '../../assets/images/Done.svg';
import { ReactComponent as NoRecordsFoundIcon } from '../../../src/assets/images/no-records-found.svg';
import { postInvoiceGlobalFactura } from '../../api/user';
import SomethingWentWrongPopUp from '../Agreement/SomethingWentWrong';
import {
  SOMETHING_WENT_WRONG,
  NOT_ALLOWED_CURSOR,
} from '../../constants/globalFacturaConstants';
import { GlobalFacturaInputStyles } from '../../JsStyles/GlobalFacturaInputStyles';

interface SearchResult {
  receiptId: string;
  date: string;
  storeNumber: string;
  customerName: string;
  totalAmount: string;
  uuid: string;
  creditMemoUuid?: string;
}

interface GlobalFacturaSearchResultProps {
  results: SearchResult[];
  onClear: () => void;
  onApply: () => void;
  onLoadMore?: () => void;
  hasMoreData?: boolean;
  loadingMore?: boolean;
}

type SortField = 'customerName' | 'totalAmount';
type SortDirection = 'asc' | 'desc';

export const GlobalFacturaSearchResult: React.FC<GlobalFacturaSearchResultProps> =
  ({ results, onLoadMore, hasMoreData = true, loadingMore = false }) => {
    const { t } = useTranslation('translation');
    const classes = GlobalFacturaInputStyles();
    const [sortField, setSortField] = useState<SortField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [editingRow, setEditingRow] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [updatedRows, setUpdatedRows] = useState<Set<string>>(new Set());
    const [uuidValues, setUuidValues] = useState<{ [key: string]: string }>({});
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkUuidValue, setBulkUuidValue] = useState('');
    const [modalView, setModalView] = useState<'input' | 'confirm' | 'success'>(
      'input'
    );
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [singleUpdateLoading, setSingleUpdateLoading] = useState(false);
    const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleScroll = () => {
        if (
          !tableContainerRef.current ||
          !onLoadMore ||
          !hasMoreData ||
          loadingMore
        )
          return;

        const { scrollTop, scrollHeight, clientHeight } =
          tableContainerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          onLoadMore();
        }
      };

      const container = tableContainerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }, [onLoadMore, hasMoreData, loadingMore]);

    const handleSort = (field: SortField) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    };

    const sortedResults = [...results].sort((a, b) => {
      if (!sortField) return 0;

      const aValue =
        sortField === 'customerName'
          ? a.customerName
          : parseFloat(a.totalAmount.replace(/[$,\s]/g, ''));
      const bValue =
        sortField === 'customerName'
          ? b.customerName
          : parseFloat(b.totalAmount.replace(/[$,\s]/g, ''));

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    const handleSelectAll = () => {
      if (selectAll) {
        setSelectedRows(new Set());
      } else {
        // Only select rows that are not updated (not disabled)
        const selectableRows = results
          .filter((result) => !updatedRows.has(result.receiptId))
          .map((result) => result.receiptId);
        setSelectedRows(new Set(selectableRows));
      }
      setSelectAll(!selectAll);
    };

    const handleRowSelect = (receiptId: string) => {
      if (updatedRows.has(receiptId)) return;
      const newSelectedRows = new Set(selectedRows);
      if (newSelectedRows.has(receiptId)) {
        newSelectedRows.delete(receiptId);
      } else {
        newSelectedRows.add(receiptId);
      }
      setSelectedRows(newSelectedRows);
      setSelectAll(
        newSelectedRows.size ===
          results.filter((result) => !updatedRows.has(result.receiptId)).length
      );
    };

    const handleEditClick = (receiptId: string, currentUuid: string) => {
      setEditingRow(receiptId);
      const existingValue = uuidValues[receiptId] || currentUuid;
      const valueToEdit = existingValue === '-' ? '' : existingValue;
      setEditValue(valueToEdit);
    };

    const handleCancelEdit = () => {
      setEditingRow(null);
      setEditValue('');
    };

    const handleConfirmEdit = () => {
      setShowConfirmModal(true);
    };

    const handleSingleUpdate = async () => {
      setSingleUpdateLoading(true);

      try {
        const payload = {
          backOfficeUUID: [
            {
              uuidUpdate: [
                {
                  receiptId: [editingRow],
                  uuid: editValue,
                },
              ],
            },
          ],
        };

        const response = await postInvoiceGlobalFactura(payload);

        if (response?.error || response?.errors || response?.data?.errors) {
          setErrorMessage(SOMETHING_WENT_WRONG);
          setShowErrorPopup(true);
        } else {
          if (editingRow) {
            setUuidValues((prev) => ({ ...prev, [editingRow]: editValue }));
            setUpdatedRows((prev) => new Set([...prev, editingRow]));
          }
          setShowBulkModal(true);
          setModalView('success');
        }
      } catch (error) {
        setErrorMessage(SOMETHING_WENT_WRONG);
        setShowErrorPopup(true);
      } finally {
        setSingleUpdateLoading(false);
      }

      setEditingRow(null);
      setEditValue('');
    };

    const handleUpdateConfirm = async () => {
      if (editingRow) {
        await handleSingleUpdate();
      }
      setShowConfirmModal(false);
    };

    const handleUpdateCancel = () => {
      setShowConfirmModal(false);
    };

    const handleBulkCancel = () => {
      setSelectedRows(new Set());
      setSelectAll(false);
    };

    const handleBulkUpdate = () => {
      setShowBulkModal(true);
      setModalView('input');
      setBulkUuidValue('');
    };

    const handleBulkModalCancel = () => {
      setShowBulkModal(false);
      setModalView('input');
    };

    const handleUpdateAll = () => {
      setModalView('confirm');
    };

    const updateLocalStateAfterBulkSuccess = () => {
      selectedRows.forEach((receiptId) => {
        setUuidValues((prev) => ({ ...prev, [receiptId]: bulkUuidValue }));
        setUpdatedRows((prev) => new Set([...prev, receiptId]));
      });
      setSelectedRows(new Set());
      setSelectAll(false);
      setModalView('success');
    };

    const renderTableCell = (content: React.ReactNode) => (
      <RACTableCell className={classes.cellStyle}>
        <Typography>{content}</Typography>
      </RACTableCell>
    );

    const renderErrorHandling = () => {
      setErrorMessage(SOMETHING_WENT_WRONG);
      setShowErrorPopup(true);
      setShowBulkModal(false);
      setModalView('input');
    };

    const handleBulkConfirm = async () => {
      setBulkUpdateLoading(true);
      try {
        const payload = {
          backOfficeUUID: [
            {
              uuidUpdate: [
                {
                  receiptId: Array.from(selectedRows),
                  uuid: bulkUuidValue,
                },
              ],
            },
          ],
        };

        const response = await postInvoiceGlobalFactura(payload);

        if (response?.error || response?.errors || response?.data?.errors) {
          renderErrorHandling();
          return;
        }
        updateLocalStateAfterBulkSuccess();
      } catch (error) {
        renderErrorHandling();
      } finally {
        setBulkUpdateLoading(false);
      }
    };

    const handleSuccessOk = () => {
      setShowBulkModal(false);
      setModalView('input');
    };

    const renderUuidWithTooltip = (uuid: string, maxLength = 8) => {
      if (!uuid || uuid === '-' || uuid.length <= maxLength) {
        return <Typography>{uuid}</Typography>;
      }
      return (
        <Tooltip title={uuid} placement="top">
          <Typography
            style={{
              cursor: 'pointer',
            }}
          >
            {uuid.substring(0, maxLength)}...
          </Typography>
        </Tooltip>
      );
    };

    const confirmationModal = () => {
      return (
        <Grid container>
          <Grid
            item
            xs={12}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px',
            }}
          >
            <NoRecordsFoundIcon
              style={{ fontSize: '48px', color: '#ff9800' }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            style={{
              textAlign: 'center',
              marginBottom: '30px',
            }}
          >
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {t('Are you sure to update the UUID?')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}
          >
            <RACButton
              variant="outlined"
              color="primary"
              onClick={handleUpdateCancel}
            >
              {t('Cancel')}
            </RACButton>
            <RACButton
              variant="contained"
              color="primary"
              onClick={handleUpdateConfirm}
              disabled={singleUpdateLoading}
              style={{ backgroundColor: classes.buttonBackgroundColor }}
            >
              {singleUpdateLoading ? (
                <CircularProgress
                  size={20}
                  style={{ color: classes.whiteTextColor }}
                />
              ) : (
                t('Update')
              )}
            </RACButton>
          </Grid>
        </Grid>
      );
    };

    const renderModalInput = () => (
      <Grid container>
        <Grid item xs={12} style={{ marginBottom: '24px' }}>
          <Typography
            variant="h6"
            style={{ fontWeight: 600, marginBottom: '16px' }}
          >
            {t('UUID Updation')}
          </Typography>
          <Typography style={{ marginBottom: '8px', fontWeight: 500 }}>
            UUID
          </Typography>
          <RACTextField
            type="text"
            value={bulkUuidValue}
            onChange={(value: string) => {
              const filteredValue = value.replace(/[^a-zA-Z0-9-]/g, '');
              setBulkUuidValue(filteredValue);
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <RACButton
            variant="outlined"
            color="primary"
            onClick={handleBulkModalCancel}
          >
            {t('Cancel')}
          </RACButton>
          <RACButton
            variant="contained"
            color="primary"
            onClick={handleUpdateAll}
            disabled={!bulkUuidValue || bulkUuidValue.trim() === ''}
            style={{ backgroundColor: classes.buttonBackgroundColor }}
          >
            {t('Update All')}
          </RACButton>
        </Grid>
      </Grid>
    );

    const renderModalConfirm = () => (
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <NoRecordsFoundIcon style={{ fontSize: '48px', color: '#ff9800' }} />
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            textAlign: 'center',
            marginBottom: '30px',
          }}
        >
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {t('Are you sure to update multiple UUID?')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}
        >
          <RACButton
            variant="outlined"
            color="primary"
            onClick={handleBulkModalCancel}
          >
            {t('Cancel')}
          </RACButton>
          <RACButton
            variant="contained"
            color="primary"
            onClick={handleBulkConfirm}
            disabled={bulkUpdateLoading}
            style={{ backgroundColor: classes.buttonBackgroundColor }}
          >
            {bulkUpdateLoading ? (
              <CircularProgress
                size={20}
                style={{ color: classes.whiteTextColor }}
              />
            ) : (
              t('Update All')
            )}
          </RACButton>
        </Grid>
      </Grid>
    );

    const renderModalSuccess = () => (
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <DoneIcon style={{ fontSize: '48px', color: '#4caf50' }} />
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            textAlign: 'center',
            marginBottom: '30px',
          }}
        >
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {t('UUID has been updated successfully')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <RACButton
            variant="contained"
            color="primary"
            onClick={handleSuccessOk}
            style={{ backgroundColor: classes.buttonBackgroundColor }}
          >
            OK
          </RACButton>
        </Grid>
      </Grid>
    );

    const renderEditActions = (result: SearchResult) => {
      if (updatedRows.has(result.receiptId)) {
        return (
          <Box style={{ opacity: 0.5, cursor: NOT_ALLOWED_CURSOR }}>✏️</Box>
        );
      }

      if (editingRow === result.receiptId) {
        return (
          <Box style={{ display: 'flex', gap: '8px' }}>
            <Box
              style={{
                cursor: 'pointer',
                color: 'red',
                fontSize: '16px',
              }}
              onClick={handleCancelEdit}
            >
              ✖
            </Box>
            <Box
              style={{
                cursor: editValue !== '' ? 'pointer' : NOT_ALLOWED_CURSOR,
                color: editValue !== '' ? 'green' : '#ccc',
                fontSize: '16px',
              }}
              onClick={editValue !== '' ? handleConfirmEdit : undefined}
            >
              <TickIcon />
            </Box>
          </Box>
        );
      }

      return (
        <Box
          style={{
            width: '20px',
            height: '20px',
            cursor: selectedRows.has(result.receiptId)
              ? NOT_ALLOWED_CURSOR
              : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: selectedRows.has(result.receiptId) ? 0.5 : 1,
          }}
          onClick={() => {
            if (!selectedRows.has(result.receiptId)) {
              handleEditClick(result.receiptId, result.uuid);
            }
          }}
        >
          <EditIcon />
        </Box>
      );
    };

    const renderUuidCell = (result: SearchResult) => {
      if (editingRow === result.receiptId) {
        return (
          <RACTextField
            type="text"
            value={editValue}
            onChange={(value: string) => {
              const filteredValue = value.replace(/[^a-zA-Z0-9-]/g, '');
              setEditValue(filteredValue);
            }}
          />
        );
      }

      return renderUuidWithTooltip(uuidValues[result.receiptId] || result.uuid);
    };
    const bulkModalContent = () => {
      switch (modalView) {
        case 'input':
          return renderModalInput();
        case 'confirm':
          return renderModalConfirm();
        default:
          return renderModalSuccess();
      }
    };
    const searchResultsTableHead = () => (
      <>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            width: '50px',
            border: 'none',
          }}
        >
          <RACCheckBox checked={selectAll} onChange={handleSelectAll} />
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            border: 'none',
          }}
        >
          {t('Receipt ID')}
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            border: 'none',
          }}
        >
          {t('Date')}
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            border: 'none',
          }}
        >
          {t('Store#')}
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => handleSort('customerName')}
        >
          {t('Customer Name')}{' '}
          <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>
            {sortField === 'customerName'
              ? sortDirection === 'asc'
                ? '↓'
                : '↑'
              : '↑'}
          </span>
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => handleSort('totalAmount')}
        >
          {t('Total Amount Collected')}{' '}
          <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>
            {sortField === 'totalAmount'
              ? sortDirection === 'asc'
                ? '↓'
                : '↑'
              : '↑'}
          </span>
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            border: 'none',
          }}
        >
          UUID
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            border: 'none',
          }}
        >
          {t('Action')}
        </RACTableCell>
      </>
    );

    const searchResultsTableBody = () => (
      <>
        {sortedResults.map((result) => (
          <RACTableRow
            key={result.receiptId}
            style={{ backgroundColor: '#fff' }}
          >
            <RACTableCell style={{ padding: '12px', width: '50px' }}>
              <RACCheckBox
                checked={selectedRows.has(result.receiptId)}
                onChange={() => handleRowSelect(result.receiptId)}
                disabled={updatedRows.has(result.receiptId)}
              />
            </RACTableCell>
            {renderTableCell(result.receiptId)}
            {renderTableCell(result.date)}
            {renderTableCell(result.storeNumber)}
            {renderTableCell(result.customerName)}
            {renderTableCell(result.totalAmount)}
            <RACTableCell className={classes.cellStyle}>
              {renderUuidCell(result)}
            </RACTableCell>
            <RACTableCell style={{ padding: '12px' }}>
              {renderEditActions(result)}
            </RACTableCell>
          </RACTableRow>
        ))}
      </>
    );

    return (
      <Grid container style={{ padding: '16px', backgroundColor: '#f5f5f5' }}>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            style={{ marginBottom: '16px', fontWeight: 600 }}
          >
            {t('Search Results')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <div
            ref={tableContainerRef}
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              borderRadius: '15px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <RACTable
              style={{
                backgroundColor: '#fff',
                marginBottom: '16px',
                overflow: 'hidden',
              }}
              renderTableHead={searchResultsTableHead}
              renderTableContent={() => (
                <>
                  {searchResultsTableBody()}
                  {loadingMore && (
                    <RACTableRow style={{ backgroundColor: '#fff' }}>
                      <RACTableCell
                        colSpan={8}
                        style={{
                          textAlign: 'center',
                          padding: '20px',
                        }}
                      >
                        <CircularProgress
                          size={24}
                          style={{
                            color: '#4285f4',
                          }}
                        />
                      </RACTableCell>
                    </RACTableRow>
                  )}
                </>
              )}
            />
          </div>
        </Grid>

        {selectedRows.size >= 1 && (
          <Grid
            item
            xs={12}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '16px',
            }}
          >
            <RACButton
              variant="outlined"
              color="primary"
              onClick={handleBulkCancel}
            >
              {t('Cancel')}
            </RACButton>
            <RACButton
              variant="contained"
              color="primary"
              onClick={handleBulkUpdate}
              style={{ backgroundColor: classes.buttonBackgroundColor }}
            >
              {t('Update UUID')}
            </RACButton>
          </Grid>
        )}

        <RACModalCard
          isOpen={showConfirmModal}
          title=""
          maxWidth="xs"
          onClose={handleUpdateCancel}
          closeIcon={true}
          borderRadius="8px"
        >
          {confirmationModal()}
        </RACModalCard>

        <RACModalCard
          isOpen={showBulkModal}
          title=""
          maxWidth="sm"
          onClose={handleBulkModalCancel}
          closeIcon={true}
          borderRadius="8px"
        >
          {bulkModalContent()}
        </RACModalCard>

        {errorMessage === SOMETHING_WENT_WRONG || !errorMessage ? (
          <SomethingWentWrongPopUp
            tab={showErrorPopup}
            setFunction={setShowErrorPopup}
          />
        ) : (
          <RACModalCard
            isOpen={showErrorPopup}
            closeIcon={false}
            maxWidth="xs"
            borderRadius="15px"
          >
            <Grid style={{ padding: '24px', textAlign: 'center' }}>
              <Typography
                variant="body1"
                style={{ marginBottom: '24px', color: '#666' }}
              >
                {errorMessage}
              </Typography>
              <RACButton
                variant="contained"
                color="primary"
                onClick={() => setShowErrorPopup(false)}
                style={{ borderRadius: '5px' }}
              >
                OK
              </RACButton>
            </Grid>
          </RACModalCard>
        )}
      </Grid>
    );
  };
